import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { AlertCircle, ArrowRight, CheckCircle2, LoaderCircle, LockKeyhole, Upload } from 'lucide-react'
import { site } from '../content/site'
import { submitLead, uploadLeadPhoto } from '../lib/api'
import { captureTracking, clearTracking, trackEvent } from '../lib/tracking'
import type { TrackingParams } from '../types'

type FormStatus =
  | { state: 'idle' }
  | { state: 'submitting'; message: string }
  | { state: 'success'; leadId: string }
  | { state: 'error'; message: string }

const maximumPhotoSize = 8 * 1024 * 1024
const maximumPhotoCount = 3

function readText(data: FormData, key: string): string | undefined {
  const value = data.get(key)
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed || undefined
}

function getSelectedPhotos(form: HTMLFormElement): File[] {
  const input = form.elements.namedItem('photos') as HTMLInputElement
  return Array.from(input.files ?? []).filter((file) => file.size > 0)
}

function validatePhotos(photos: File[]): string | null {
  if (photos.length > maximumPhotoCount) return `Choose up to ${maximumPhotoCount} photos.`
  if (photos.some((photo) => !photo.type.startsWith('image/'))) return 'Photos must be image files.'
  if (photos.some((photo) => photo.size > maximumPhotoSize)) return 'Each photo must be smaller than 8 MB.'
  return null
}

export function LeadForm() {
  const [status, setStatus] = useState<FormStatus>({ state: 'idle' })
  const [photoCount, setPhotoCount] = useState(0)
  const startedAt = useRef<number | null>(null)
  const tracking = useRef<TrackingParams>({})

  useEffect(() => {
    startedAt.current = Date.now()
    tracking.current = captureTracking()
    trackEvent('form_view')
  }, [])

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const data = new FormData(form)
    const photos = getSelectedPhotos(form)
    const photoError = validatePhotos(photos)

    if (photoError) {
      setStatus({ state: 'error', message: photoError })
      return
    }

    setStatus({
      state: 'submitting',
      message: photos.length > 0 ? 'Uploading photos…' : 'Sending your request…',
    })

    try {
      const uploadSession = crypto.randomUUID()
      const photoKeys: string[] = []

      for (const photo of photos) {
        photoKeys.push(await uploadLeadPhoto(photo, uploadSession))
      }

      if (photos.length > 0) {
        setStatus({ state: 'submitting', message: 'Sending your request…' })
      }

      const name = readText(data, 'name')
      const email = readText(data, 'email')
      const serviceType = readText(data, 'serviceType')
      const message = readText(data, 'message')

      if (!name || !email || !serviceType || !message) {
        throw new Error('Please complete the required fields.')
      }

      const leadId = await submitLead({
        name,
        email,
        serviceType,
        message,
        phone: readText(data, 'phone'),
        subject: readText(data, 'subject'),
        aquariumSize: readText(data, 'aquariumSize'),
        aquariumType: readText(data, 'aquariumType'),
        waterType: readText(data, 'waterType'),
        zipCode: readText(data, 'zipCode'),
        urgency: readText(data, 'urgency'),
        budget: readText(data, 'budget'),
        photoKeys,
        uploadSession: photoKeys.length > 0 ? uploadSession : undefined,
        company: readText(data, 'company'),
        formStartedAt: startedAt.current ?? Date.now(),
        ...tracking.current,
      })

      form.reset()
      setPhotoCount(0)
      setStatus({ state: 'success', leadId })
      trackEvent('generate_lead', { lead_id: leadId, service_type: serviceType })
      clearTracking()

      const conversionId = import.meta.env.VITE_AW_CONVERSION_ID
      if (conversionId && window.gtag) {
        window.gtag('event', 'conversion', { send_to: conversionId, value: 1, currency: 'USD' })
      }
    } catch (error) {
      setStatus({
        state: 'error',
        message: error instanceof Error
          ? error.message
          : 'Something went wrong. Please call us or try again.',
      })
      trackEvent('form_error')
    }
  }

  if (status.state === 'success') {
    return (
      <div className="form-success" role="status">
        <CheckCircle2 aria-hidden="true" />
        <p className="eyebrow">Request received</p>
        <h2>Thanks. Brian will review the details personally.</h2>
        <p>Expect a call or email within one business day. If this is an active leak or livestock emergency, call now.</p>
        <a className="button button--dark" href={site.phoneHref}>{site.phoneDisplay}</a>
        <small>Reference: {status.leadId}</small>
      </div>
    )
  }

  const isSubmitting = status.state === 'submitting'

  return (
    <form className="lead-form" onSubmit={(event) => void handleSubmit(event)}>
      <div className="lead-form__heading">
        <p className="eyebrow">Project request</p>
        <h2>Tell us what you’re working with.</h2>
        <p>Required fields are marked with an asterisk. Photos and project details are optional.</p>
      </div>

      <fieldset>
        <legend>How can we help?</legend>
        <div className="choice-grid">
          {[
            ['new-install', 'New aquarium', 'Design and professional installation'],
            ['maintenance', 'Maintenance', 'Cleaning, testing, and ongoing care'],
            ['emergency', 'Emergency repair', 'Leaks, equipment, or livestock health'],
            ['consultation', 'Consultation', 'Planning or a second opinion'],
          ].map(([value, title, description]) => (
            <label className="choice-card" key={value}>
              <input type="radio" name="serviceType" value={value} required />
              <span><strong>{title}</strong><small>{description}</small></span>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset>
        <legend>Your contact details</legend>
        <div className="field-grid field-grid--two">
          <label className="field">
            <span>Name *</span>
            <input name="name" autoComplete="name" required minLength={2} maxLength={100} />
          </label>
          <label className="field">
            <span>Email *</span>
            <input name="email" type="email" autoComplete="email" required maxLength={254} />
          </label>
          <label className="field">
            <span>Phone</span>
            <input name="phone" type="tel" autoComplete="tel" maxLength={30} />
          </label>
          <label className="field">
            <span>ZIP code</span>
            <input name="zipCode" inputMode="numeric" autoComplete="postal-code" pattern="[0-9]{5}(-[0-9]{4})?" maxLength={10} />
          </label>
        </div>
      </fieldset>

      <fieldset>
        <legend>What should we know?</legend>
        <div className="field-grid">
          <label className="field">
            <span>Subject</span>
            <input name="subject" maxLength={160} placeholder="A short summary of your request" />
          </label>
          <label className="field">
            <span>Message *</span>
            <textarea
              name="message"
              required
              minLength={10}
              maxLength={5000}
              rows={6}
              placeholder="Describe the aquarium, the space, or the problem you’re seeing."
            />
          </label>
        </div>
      </fieldset>

      <details className="optional-details">
        <summary>Optional aquarium, timing & budget details</summary>
        <div className="field-grid field-grid--two">
          <label className="field">
            <span>Aquarium size</span>
            <select name="aquariumSize" defaultValue="">
              <option value="">Choose a range</option>
              <option value="under-50">Under 50 gallons</option>
              <option value="50-150">50–150 gallons</option>
              <option value="150-500">150–500 gallons</option>
              <option value="500-plus">500+ gallons</option>
              <option value="custom-unsure">Custom / not sure</option>
            </select>
          </label>
          <label className="field">
            <span>Aquarium type</span>
            <select name="aquariumType" defaultValue="">
              <option value="">Choose a type</option>
              <option value="reef">Reef tank</option>
              <option value="fish-only">Fish only</option>
              <option value="planted">Planted tank</option>
              <option value="pond">Pond / water feature</option>
              <option value="other">Other / not sure</option>
            </select>
          </label>
          <label className="field">
            <span>Water type</span>
            <select name="waterType" defaultValue="">
              <option value="">Choose one</option>
              <option value="saltwater">Saltwater</option>
              <option value="freshwater">Freshwater</option>
              <option value="unsure">Not sure</option>
            </select>
          </label>
          <label className="field">
            <span>Timing</span>
            <select name="urgency" defaultValue="">
              <option value="">Choose one</option>
              <option value="asap">ASAP / emergency</option>
              <option value="this-week">This week</option>
              <option value="this-month">This month</option>
              <option value="flexible">Flexible / exploring</option>
            </select>
          </label>
          <label className="field field--wide">
            <span>Budget range</span>
            <select name="budget" defaultValue="">
              <option value="">Prefer to discuss</option>
              <option value="under-1k">Under $1,000</option>
              <option value="1k-5k">$1,000–$5,000</option>
              <option value="5k-15k">$5,000–$15,000</option>
              <option value="15k-50k">$15,000–$50,000</option>
              <option value="over-50k">$50,000+</option>
            </select>
          </label>
        </div>
      </details>

      <label className="photo-field">
        <Upload aria-hidden="true" />
        <span>
          <strong>Add up to three photos</strong>
          <small>{photoCount > 0 ? `${photoCount} selected` : 'JPEG, PNG, HEIC, or WebP · 8 MB each'}</small>
        </span>
        <input
          name="photos"
          type="file"
          accept="image/jpeg,image/png,image/heic,image/webp"
          multiple
          onChange={(event) => setPhotoCount(event.currentTarget.files?.length ?? 0)}
        />
      </label>

      <label className="honeypot" aria-hidden="true">
        Company website
        <input name="company" tabIndex={-1} autoComplete="off" />
      </label>

      {status.state === 'error' ? (
        <div className="form-message form-message--error" role="alert">
          <AlertCircle aria-hidden="true" /> {status.message}
        </div>
      ) : null}

      <div className="lead-form__footer">
        <p><LockKeyhole aria-hidden="true" /> Your details are used only to assess and respond to this request.</p>
        <button className="button button--accent" type="submit" disabled={isSubmitting}>
          {isSubmitting ? <LoaderCircle className="spin" aria-hidden="true" /> : null}
          {isSubmitting ? status.message : 'Send my request'}
          {!isSubmitting ? <ArrowRight aria-hidden="true" /> : null}
        </button>
      </div>
    </form>
  )
}
